import { action, runInAction, makeObservable, when } from 'mobx';
import * as Sentry from '@sentry/react';
import { IProcessoDto } from '../api/dto/ProcessoDto';
import { ISearch, Processo, TipoControle, TipoMinuta } from '../modelo';
import { SITUACOES_DE_MINUTA } from '../modelo/SituacaoMinuta';
import { AppStore } from '../store/AppStore';
import ControleGabineteStore from '../store/ControleGabineteStore';
import EscaninhoStore from '../store/EscaninhoStore';
import FiltroArvorePeticaoStore from '../store/FiltroArvorePeticaoStore';
import FiltroStore from '../store/FiltroStore';
import GabineteStore from '../store/GabineteStore';
import ProcessoExpandidoStore from '../store/ProcessoExpandidoStore';
import ProcessoStore from '../store/ProcessoStore';
import RouterStore from '../store/RouterStore';
import UsuarioStore from '../store/UsuarioStore';
import UtilNumeroProcesso from '../utils/UtilNumeroProcesso';
import ClienteEnvelopeService from './ClienteEnvelopeService';
import EscaninhoPeticaoService from './EscaninhoPeticaoService';
import FiltroService from './FiltroService';
import ImportacaoService from './ImportacaoService';
import JulgamentoService from './JulgamentoService';
import MinutaService from './MinutaService';
import PeticaoService from './PeticaoService';
import ProcessoExpandidoService from './ProcessoExpandidoService';
import ProcessoService from './ProcessoService';
import MapeadorProcesso from './mapeadores/MapeadorProcesso';
import InformacoesProcessoService from './InformacoesProcessoService';
import ImpedimentoService from './ImpedimentoService';

class EscaninhoService {
    executando = false;
    controlerCarregamentoProcessos: AbortController;
    private readonly timeoutLoteProcessosMs = 120000;
    private readonly timeoutAcompanharGabineteMs = 30000;
    private readonly timeoutBuscaLotesMs = 60000;
    private readonly timeoutAguardarGabineteMs = 30000;

    private async comTimeout<T>(promise: Promise<T>, timeoutMs: number, mensagem: string): Promise<T> {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        const timeoutPromise = new Promise<T>((_, reject) => {
            timeoutId = setTimeout(() => reject(new Error(mensagem)), timeoutMs);
        });

        try {
            return await Promise.race([promise, timeoutPromise]);
        } finally {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        }
    }

    private async aguardarGabineteDisponivel(maxWaitMs: number = this.timeoutAguardarGabineteMs): Promise<boolean> {
        if (UsuarioStore?.gabinete) {
            return true;
        }

        try {
            await when(() => !!UsuarioStore?.gabinete, { timeout: maxWaitMs });
            return true;
        } catch {
            return false;
        }
    }

    private async aguardarProximoTick(): Promise<void> {
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }

    private async aguardarPrimeiraPintura(): Promise<void> {
        await new Promise<void>((resolve) => {
            if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(() => resolve());
                });
                return;
            }

            setTimeout(resolve, 0);
        });
    }

    private adicionarProcessosSemDuplicar(processosBase: IProcessoDto[], processosAdicionais: IProcessoDto[]): IProcessoDto[] {
        const processosPorId = new Map<number, IProcessoDto>();

        processosBase
            .filter(processo => !!processo?.identificadorProcesso)
            .forEach(processo => processosPorId.set(processo.identificadorProcesso, processo));

        processosAdicionais
            .filter(processo => !!processo?.identificadorProcesso)
            .forEach(processo => {
                const processoExistente = processosPorId.get(processo.identificadorProcesso);

                if (!processoExistente) {
                    processosPorId.set(processo.identificadorProcesso, processo);
                }
            });

        return Array.from(processosPorId.values());
    }

    private async mapearProcessosSemBloquear(processosBrutos: any[], tamanhoLote = 250): Promise<Processo[]> {
        const processosMapeados: Processo[] = [];

        for (let i = 0; i < processosBrutos.length; i += tamanhoLote) {
            const lote = processosBrutos.slice(i, i + tamanhoLote);
            processosMapeados.push(...MapeadorProcesso.toProcessos(lote));

            if (i + tamanhoLote < processosBrutos.length) {
                await this.aguardarProximoTick();
            }
        }

        return processosMapeados;
    }

    constructor() {
        makeObservable(this, {
            retirarProcessos: action,
            mergeDadosDoProcesso: action,
            carregarProcessoStore: action,
            abrirNoEscaninho: action,
            filtrarEscaninhoPorAgrupadoresPadroes: action
        });
    }

    retirarProcessos(idsProcesso: number[]) {
        idsProcesso.forEach(idProcesso => {
            const index = ProcessoStore._processos.findIndex(procStore => procStore.identificadorProcesso === idProcesso);
            ProcessoStore._processos.splice(index, 1);
        });
    }

    async atualizarProcessos(processos: Partial<Processo>[]) {
        for (let i = 0; i < processos.length; i++) {
            const proc = processos[i];
            await this.atualizarProcesso(proc.identificadorProcesso, true);
        }
    }

    async atualizarProcesso(idProcesso: number, carregarInteiro: boolean): Promise<void> {
        const resultadoConsulta = await ProcessoService.processoPorIdFiltroRapido(UsuarioStore.gabinete, idProcesso, {
            controleNumero: RouterStore.isEscaninhoDeVotoVencido ? ControleGabineteStore.buscarPorTipo(TipoControle.VOTOVENCIDO).numero : undefined
        } as ISearch);

        if (resultadoConsulta?.length == 0) {
            const indexParaExclusao = ProcessoStore._processos.findIndex(proc => proc.identificadorProcesso === idProcesso);
            runInAction(() => {
                ProcessoStore._processos.splice(indexParaExclusao, 1);
            });
            return;
        }
        const processo = (resultadoConsulta as any).resultado != undefined ? (resultadoConsulta as any).resultado[0] : resultadoConsulta[0];
        const dadosPeticoes = await PeticaoService.carregarPeticoes(processo);
        runInAction(() => {
            processo.pecasPeticoesPendentes = dadosPeticoes.peticoesPendentes || [];
        });
        await JulgamentoService.carregarTipoJulgamento([processo.identificadorProcesso]);

        const resultadoJulgamento = await InformacoesProcessoService.buscarResultadoDeJulgamentoDaListaDeProcessos([idProcesso]);
        const resultadoAgProvido = await InformacoesProcessoService.buscarProcessosComAgProvido([idProcesso]);
        runInAction(() => {
            ProcessoStore._processos.forEach(processoItem => {
                // Verifica se o identificador do processo atual corresponde ao que foi buscado
                if (processoItem.identificadorProcesso === idProcesso) {
                    // Atribui o resultado diretamente ao processo correspondente
                    processoItem.resultadoJulgamentoProcesso = resultadoJulgamento[idProcesso] || [];
                    processoItem.agravoProvido = resultadoAgProvido[idProcesso];
                }
            });
        });
        if (RouterStore.isEscaninhoPeticao) {
            runInAction(() => {
                ProcessoStore.carregandoPeticoesPendentes = true;
            });
        }
        this.atualizarProcessoNaStore(processo, carregarInteiro);
        if (RouterStore.isEscaninhoPeticao) {
            await EscaninhoPeticaoService.carregarPeticoesPendentesPorProcesso(idProcesso);
        }
    }

    async atualizarProcessosLote(ids: number[], carregarInteiro: boolean): Promise<void> {
        const resultadoConsulta =
            await ProcessoService.processoPorIdsFiltroRapido(UsuarioStore.gabinete, ids, {
                controleNumero: RouterStore.isEscaninhoDeVotoVencido ? ControleGabineteStore.buscarPorTipo(TipoControle.VOTOVENCIDO).numero : undefined
            } as ISearch);

        ids.forEach(async idProcesso => {
            if (resultadoConsulta?.length == 0) {
                const indexParaExclusao = ProcessoStore._processos.findIndex(proc => proc.identificadorProcesso === idProcesso);
                runInAction(() => {
                    ProcessoStore._processos.splice(indexParaExclusao, 1);
                });
                return;
            }
            const processo = Processo.novo((resultadoConsulta as any).resultado != undefined ? (resultadoConsulta as any).resultado[0] : resultadoConsulta[0]);
            const dadosPeticoes = await PeticaoService.carregarPeticoes(processo);
            runInAction(() => {
                processo.pecasPeticoesPendentes = dadosPeticoes.peticoesPendentes || [];
            });
            await JulgamentoService.carregarTipoJulgamento([processo.identificadorProcesso]);
            await this.atualizarProcessoNaStore(processo, carregarInteiro);
        });
    }

    atualizarProcessoNaStore = async (processo: Processo, carregarInteiro: boolean) => {
        const idProcesso = processo.identificadorProcesso;
        runInAction(() => {
            let index = ProcessoStore._processos.findIndex(procStore => procStore.identificadorProcesso === idProcesso);
            if (!index && index !== 0) {
                index = ProcessoStore._processos.length;
                ProcessoStore._processos.splice(index, 0, processo);
            }
            this.mergeDadosDoProcesso(processo, ProcessoStore._processos[index]);
            if (!processo) {
                ProcessoStore._processos.splice(index, 1);
            } else {
                ProcessoStore._processos.splice(index, 1, processo);
                processo.setBackground();
            }
        });
        if (carregarInteiro) {
            await this.funcoesDeCarregamentoDeProcesso(processo);
            if (ProcessoExpandidoStore.deveRenderizar(processo.identificadorProcesso)) {
                await ProcessoExpandidoService.consultarDadosProcesso(processo.identificadorProcesso);
            }
        }
    }

    mergeDadosDoProcesso = (processoNovo: Processo, processoComDados: Processo) => {
        if (!processoNovo || !processoComDados) {
            return;
        }

        // processoNovo.assinavel = processoComDados.minutaParaAssinarDto?.assinavel || processoComDados.assinavel;
        // processoNovo.motivoNaoAssinavel = processoComDados.minutaParaAssinarDto?.motivoNaoAssinavel || processoComDados.motivoNaoAssinavel;
        processoNovo.agravoProvido = processoNovo.agravoProvido || processoComDados.agravoProvido;
        processoNovo.tipoJulgamento = processoNovo.tipoJulgamento || processoComDados.tipoJulgamento;
        processoNovo.backgroundPadrao = processoNovo.backgroundPadrao || processoComDados.backgroundPadrao;
        processoNovo.assuntosProcesso = processoNovo.assuntosProcesso?.length ? processoNovo.assuntosProcesso : processoComDados.assuntosProcesso;
        processoNovo.partes = processoNovo.partes || processoComDados.partes;
        processoNovo.indicadores = processoNovo.indicadores?.length > 0 ? processoNovo.indicadores : processoComDados.indicadores;
        processoNovo.peticoesPendentes = processoNovo.peticoesPendentes?.length > 0  ?
            processoNovo.peticoesPendentes
            :
            processoComDados.peticoesPendentes;
        processoNovo.pecasPeticoesPendentes = processoNovo.pecasPeticoesPendentes?.length > 0 ? processoNovo.pecasPeticoesPendentes : processoComDados.pecasPeticoesPendentes
        processoNovo.etiquetas = processoNovo.etiquetas.length > 0 ? processoNovo.etiquetas : processoComDados.etiquetas
        processoNovo.triagem = processoNovo.triagem || processoComDados.triagem;
        processoNovo.triagemLivre = processoNovo.triagemLivre || processoComDados.triagemLivre;
        processoNovo.favorito = processoNovo.favorito || processoComDados.favorito;
        processoNovo.retorno = processoNovo.retorno || processoComDados.retorno;
        processoNovo.idsMinutasComentadas = processoNovo.idsMinutasComentadas.length ? processoNovo.idsMinutasComentadas : processoComDados.idsMinutasComentadas;
        processoNovo.situacaoSessao = processoNovo.situacaoSessao || processoComDados.situacaoSessao;
        processoNovo.situacaoDeInclusaoEmPauta = processoNovo.situacaoDeInclusaoEmPauta || processoComDados.situacaoDeInclusaoEmPauta;
        processoNovo.pauta = processoNovo.pauta || processoComDados.pauta;
        processoNovo.dataAutuacaoTst = processoNovo.dataAutuacaoTst || processoComDados.dataAutuacaoTst;
        processoNovo.correJuntos = processoNovo.correJuntos?.length ? processoNovo.correJuntos : processoComDados.correJuntos;
        processoNovo.processosAssociados = processoNovo.processosAssociados?.length ? processoNovo.processosAssociados : processoComDados.processosAssociados;
        processoNovo.impedimentos = processoNovo.impedimentos?.length ? processoNovo.impedimentos : processoComDados.impedimentos;
        processoNovo.movimentos = processoNovo.movimentos?.length ? processoNovo.movimentos : processoComDados.movimentos;
        processoNovo.ultimoMovimentoProcessual = processoNovo.ultimoMovimentoProcessual || processoComDados.ultimoMovimentoProcessual;


        processoNovo.minutas.map(minutaNovo => {
            const minutaEncontrada = processoComDados.minutas.find(minutaComDados => minutaComDados.numero === minutaNovo.numero);
            if (minutaEncontrada) {
                minutaNovo.categorias = minutaNovo.categorias || minutaEncontrada.categorias;
                minutaNovo.movimentosCNJ = minutaNovo.movimentosCNJ || minutaEncontrada.movimentosCNJ;
            }
        });

        if (processoComDados.selecionado) {
            processoNovo.selecionar(processoComDados.selecionado);
        }
        if (FiltroStore.filtroAplicado) {
            FiltroStore.aplicarFiltros(processoNovo);
        }
    };

    //MANTEM
    async carregarProcessosDoGabinete(): Promise<any> {
        if (ProcessoStore.carregandoProcessosNoEscaninho) {
            return;
        }

        return await this.selecionaConsulta(0, true);
    }

    deselecionarProcessos = () => {
        EscaninhoStore.processosSelecionados.forEach(proc => proc.selecionar(false));
    }

    ajustarFiltros(limparFiltros: boolean = true, entradaJaPintada: boolean = false) {
        if (!ProcessoStore.carregouProcessosNoEscaninho) {
            setTimeout(() => {
                this.ajustarFiltros(limparFiltros, entradaJaPintada);
            }, 200);
            return;
        }
        limparFiltros && FiltroStore.limparFiltros();
        const tipo = EscaninhoStore.tipoEscaninho;
        switch (tipo) {
            case 'gerencial':
                runInAction(() => {
                    FiltroStore.drawerIsOpen = false;
                });
                if (!RouterStore.isImportacaoNoEscaninho) {
                    this.conferirQueryParams();
                } else {
                    ImportacaoService.verificarImportacao();
                }
                runInAction(() => {
                    if (limparFiltros) {
                        FiltroStore.classeProcessoFilter = [];
                        FiltroStore.indicadorSelecionadoFilter = [];
                        FiltroStore.orgaoJudicanteSelecionadoFilter = [];
                        EscaninhoStore.expandedCardIndicadores = false;
                        EscaninhoStore.expandedCardClasse = false;
                        EscaninhoStore.expandedCardOJC = false;
                        EscaninhoStore.expandedCardFase = false;
                        EscaninhoStore.cardPrincipalExpanded = false;
                        FiltroStore.origem = (!FiltroStore.origem || FiltroStore.origem.length == 0) ? 'PL' : FiltroStore.origem;
                        FiltroStore.incluirForaDoGabineteFilter = false;
                    }
                    FiltroStore.procurar(true, true, !entradaJaPintada);
                    GabineteStore.abrirEscaninho = true;
                });
                break;
            case 'dashboard':
                break;
            case 'assinatura':
            case 'assinaturaNova':
                runInAction(() => {
                    if (limparFiltros) {
                        FiltroStore.assinavel = true;
                        FiltroStore.incluirForaDoGabineteFilter = true;
                    }
                    FiltroService.fazerBuscaPadrao(false);
                });
                break;
            case 'usuario':
                FiltroService.fazerBuscaPadrao();
                break;
            case 'vistos':
                FiltroService.fazerBuscaPadrao(false);
                break;
            case 'filtrado':
                runInAction(() => {
                    if (limparFiltros) {
                        FiltroStore.incluirForaDoGabineteFilter = false;
                    }
                    this.conferirQueryParams();
                    FiltroService.fazerBuscaPadrao(false);
                });
                break;
            case 'julgamento':
                FiltroService.fazerBuscaPadrao(false);
                break;
            case 'peticao':
                if (limparFiltros) {
                    runInAction(() => {
                        FiltroStore.incluirForaDoGabineteFilter = true;
                        FiltroArvorePeticaoStore.drawerAberto = false;
                    });
                }
                FiltroService.fazerBuscaPadrao(false);
                break;
            case 'voto-vencido':
                if (limparFiltros) {
                    runInAction(() => {
                        FiltroStore.incluirForaDoGabineteFilter = true;
                        FiltroStore.filtroAplicado = true;
                    });
                }
                FiltroService.fazerBuscaPadrao(false);
                break;
            case 'redator-designado':
                if (limparFiltros) {
                    runInAction(() => {
                        FiltroStore.incluirForaDoGabineteFilter = true;
                        FiltroStore.filtroAplicado = true;
                    });
                }
                FiltroService.fazerBuscaPadrao(false);
                break;
            default:
                if (limparFiltros) {
                    runInAction(() => {
                        FiltroStore.incluirForaDoGabineteFilter = false;
                    });
                }
                break;
        }
        runInAction(() => {
            FiltroStore.filtroAplicado = true;
        });
    }

    //MANTEM
    async selecionaConsulta(tentativa: number = 0, silencioso: boolean = false) {
        let processos: Processo[] = [];
        const agregados = true;

        let concluiuComSucesso = false;
        let controlador: AbortController | undefined;

        try {
            const gabineteDisponivelNoInicio = await this.aguardarGabineteDisponivel();
            if (!gabineteDisponivelNoInicio) {
                throw new Error('Gabinete do usuário indisponível para carregar processos do escaninho.');
            }

            if (ProcessoStore.carregandoProcessosNoEscaninho) {
                this.controlerCarregamentoProcessos.abort();
            }

            this.controlerCarregamentoProcessos = new AbortController();
            controlador = this.controlerCarregamentoProcessos;

            await this.comTimeout(
                Promise.resolve(ClienteEnvelopeService.acompanharGabinete()),
                this.timeoutAcompanharGabineteMs,
                'Tempo limite ao sincronizar gabinete do usuário.'
            );

            const gabineteDisponivelAposSync = await this.aguardarGabineteDisponivel();
            if (!gabineteDisponivelAposSync) {
                throw new Error('Gabinete do usuário não foi resolvido após sincronização.');
            }

            runInAction(() => {
                ProcessoStore._processos?.map(p => {
                    Processo.limparProcesso(p)
                })
                ProcessoStore._processosForaDoGE?.map(p => {
                    Processo.limparProcesso(p);
                })
                ProcessoStore.limpar();
                ProcessoStore.carregandoProcessosNoEscaninho = true;
                ProcessoStore.carregouProcessosNoEscaninho = false;
                AppStore.isCarregandoTodosProcessos = true;
            });

            // Links para um processo específico precisam encontrá-lo antes de aplicar o filtro inicial.
            const carregamentoProgressivo = EscaninhoStore.tipoEscaninho === 'gerencial'
                && !parseInt(RouterStore.getParam('idProcesso')[0]);
            const cargaAtual = () => controlador === this.controlerCarregamentoProcessos && !controlador.signal.aborted;
            let filaPublicacao = Promise.resolve();
            const idsPublicados = new Set<number>();
            const ehDashboardAcervo = EscaninhoStore.tipoEscaninho === 'dashboard';
            if (ehDashboardAcervo) {
                // Garante que o spinner seja pintado antes de iniciar blocos mais pesados.
                await this.aguardarPrimeiraPintura();
            }

            const searchGabinete: ISearch = {
                gabinete: UsuarioStore.gabinete
            }
            let acumuladorProcessos = [];

            const promisesProcessos = await this.comTimeout(
                ProcessoService.recuperaPorSearchAsync(searchGabinete, controlador, silencioso, carregamentoProgressivo ? 20 : undefined),
                this.timeoutBuscaLotesMs,
                'Tempo limite ao buscar lotes de processos do escaninho.'
            );
            const promisesComTimeout = promisesProcessos.map(promise =>
                this.comTimeout(promise, this.timeoutLoteProcessosMs, 'Tempo limite ao carregar lote de processos.')
                    .then(lote => {
                        if (!carregamentoProgressivo || !Array.isArray(lote)) return lote;
                        // Serializa as publicações para não perder lotes que chegam juntos.
                        const publicacao = filaPublicacao.then(async () => {
                            if (!cargaAtual()) return;
                            const novos = lote.filter(p => p && !idsPublicados.has(p.identificadorProcesso));
                            const mapeados = await this.mapearProcessosSemBloquear(novos);
                            if (!cargaAtual()) return;
                            runInAction(() => {
                                mapeados.forEach(processo => {
                                    idsPublicados.add(processo.identificadorProcesso);
                                    if (FiltroStore.filtroAplicado) FiltroStore.aplicarFiltros(processo);
                                });
                                ProcessoStore._processos.push(...mapeados);
                                // Mantém o skeleton até concluir a carga da API.
                                // A exibição progressiva fica na preparação dos filtros,
                                // quando o acervo já está disponível em memória.
                            });
                            await this.aguardarProximoTick();
                        });
                        filaPublicacao = publicacao.catch(() => undefined);
                        return publicacao.then(() => lote);
                    })
            );

            const resultados = await Promise.allSettled(promisesComTimeout);
            if (!cargaAtual()) return;

            let houveFalhaDeLote = false;
            resultados.forEach((resultado) => {
                if (resultado.status === 'fulfilled') {
                    const lote = Array.isArray(resultado.value) ? resultado.value : [];
                    lote.forEach(processo => acumuladorProcessos.push(processo));
                } else {
                    houveFalhaDeLote = true;
                    this.registrarErroDeCarregamento('lote de processos', resultado.reason);
                }
            });

            if (houveFalhaDeLote && acumuladorProcessos.length === 0) {
                if (tentativa < 1) {
                    return this.selecionaConsulta(tentativa + 1, silencioso);
                }

                runInAction(() => {
                    ProcessoStore._processos = [];
                    AppStore.isCarregandoTodosProcessos = false;
                    ProcessoStore.carregandoProcessosNoEscaninho = false;
                    ProcessoStore.carregouProcessosNoEscaninho = true;
                });

                concluiuComSucesso = true;
                return { processos: [], agregados };
            }

            if (RouterStore.isEscaninhoPauta) {
                try {
                    const processosPautasFuturas = await ProcessoService.buscarProcessosPautasFuturas(UsuarioStore.gabinete);
                    acumuladorProcessos = this.adicionarProcessosSemDuplicar(acumuladorProcessos, processosPautasFuturas || []);
                } catch (erro) {
                    console.warn('Falha ao buscar processos de pautas futuras.', erro);
                }
            }

            if (carregamentoProgressivo) {
                // Preserva seleção e dados dos cards já abertos durante a carga.
                runInAction(() => {
                    ProcessoStore._processos = ProcessoStore._processos.slice().sort((a, b) =>
                        new Date(b.dataUltimaModificacao).getTime() - new Date(a.dataUltimaModificacao).getTime());
                });
            } else if (ehDashboardAcervo) {
                const processosMapeados = await this.mapearProcessosSemBloquear(acumuladorProcessos);

                runInAction(() => {
                    ProcessoStore._processos = processosMapeados;
                });

                // Ordenação e limpeza em segundo plano para manter a UI responsiva na abertura.
                setTimeout(() => {
                    runInAction(() => {
                        ProcessoStore._processos = ProcessoStore._processos
                            .slice()
                            .sort((a, b) => new Date(b.dataUltimaModificacao).getTime() - new Date(a.dataUltimaModificacao).getTime());
                        acumuladorProcessos.forEach(p => Processo.limparProcesso(p));
                    });
                }, 0);
            } else {
                const processosMapeados = await this.mapearProcessosSemBloquear(acumuladorProcessos);

                runInAction(() => {
                    ProcessoStore._processos = processosMapeados;
                    ProcessoStore._processos = ProcessoStore._processos.slice().sort((a, b) => new Date(b.dataUltimaModificacao).getTime() - new Date(a.dataUltimaModificacao).getTime());
                    acumuladorProcessos.forEach(p => Processo.limparProcesso(p));
                });
            }

            runInAction(() => {
                ProcessoStore.carregandoPeticoesPendentesEscaninho = true;
                AppStore.isCarregandoTodosProcessos = false;
                ProcessoStore.carregandoProcessosNoEscaninho = false;
                ProcessoStore.carregouProcessosNoEscaninho = true;
                //this.conferirQueryParams();
            });

            this.dispararEmBackground('dados complementares após pintura', async () => {
                await this.aguardarPrimeiraPintura();
                if (controlador.signal.aborted || controlador !== this.controlerCarregamentoProcessos) {
                    return;
                }
                this.dispararEmBackground('comentários de minutas', () => ProcessoService.carregarMinutasComentadasDeTodosOsProcessos());
                this.dispararEmBackground('categorias de minutas', () => MinutaService.carregarCategoriasDeMinutas());
                this.dispararEmBackground('triagens', () => ProcessoService.carregarTriagemTodosProcessos());
                this.dispararEmBackground('temas', () => ProcessoService.carregarTemasTodosProcessos());
                await this.carregarProcessoStore();
            });
            processos = ProcessoStore._processos;
            concluiuComSucesso = true;
        } catch (error) {
            if (controlador && (controlador.signal.aborted || controlador !== this.controlerCarregamentoProcessos)) return;
            if (silencioso) {
                console.warn('Falha ao carregar processos do escaninho (modo silencioso).', error);
            } else {
                console.error('Falha ao carregar processos do escaninho.', error);
            }

            if (tentativa < 1) {
                return this.selecionaConsulta(tentativa + 1, silencioso);
            }

            runInAction(() => {
                AppStore.isCarregandoTodosProcessos = false;
                ProcessoStore.carregandoProcessosNoEscaninho = false;
                ProcessoStore.carregouProcessosNoEscaninho = false;
            });
        } finally {
            // Garantia final para não deixar cards em skeleton infinito após F5.
            if (!concluiuComSucesso && (!controlador || controlador === this.controlerCarregamentoProcessos)) {
                runInAction(() => {
                    AppStore.isCarregandoTodosProcessos = false;
                    ProcessoStore.carregandoProcessosNoEscaninho = false;
                });
            }
        }

        // runInAction(() => {
        //     AppStore.isCarregandoProcessos = false;
        // });
        return { processos, agregados };
    }

    async carregarProcesosParaAcervo(search: ISearch) {
        const processos = await ProcessoService.recuperaPorSearch(search, this.controlerCarregamentoProcessos);
        const resultado = MapeadorProcesso.toProcessos(processos || []);
        return resultado;
    }

    /**
     * Dispara um carregamento em segundo plano sem aguardar a conclusão, garantindo
     * que uma falha (síncrona ou rejeição de promise) seja apenas registrada e não
     * vire uma unhandled rejection nem interrompa os demais carregamentos.
     */
    private dispararEmBackground(rotulo: string, executar: () => unknown): void {
        try {
            Promise.resolve(executar()).catch(erro => this.registrarErroDeCarregamento(rotulo, erro));
        } catch (erro) {
            this.registrarErroDeCarregamento(rotulo, erro);
        }
    }

    private registrarErroDeCarregamento(rotulo: string, erro: unknown): void {
        console.error(`Erro no carregamento em segundo plano: ${rotulo}`, erro);
        Sentry.captureException(erro, { level: 'warning' });
    }

    async carregarProcessoStore(): Promise<void> {
        // let processosNaPaginaAtual = ProcessoStore._processos.slice(PaginacaoStore.primeiroIndice, PaginacaoStore.ultimoIndice)
        // Promise.all([
        // Indicadores (ex.: chips "Meta CNJ"/"Meta 2" na lista) só eram buscados sob
        // demanda (abrir drawer de filtros, expandir indicadores, focar no filtro de
        // indicadores). Isso fazia com que sumissem tanto no carregamento inicial da
        // tela quanto após um recarregamento, só reaparecendo ao abrir os detalhes do
        // processo (que reaproveita o mesmo campo já carregado). Buscamos aqui também,
        // no ponto único de carregamento/recarregamento da lista.
        if (!ProcessoStore.carregouIndicadores && !ProcessoStore.carregandoIndicadores) {
            this.dispararEmBackground('indicadores', () => ProcessoService.carregarIndicadoresDeTodosOsProcessosAsync());
        }
        this.dispararEmBackground('etiquetas', () => ProcessoService.carregarEtiquetasDeTodosOsProcessos());
        this.dispararEmBackground('triagens livres', () => ProcessoService.carregarTriagensLivreDeTodosOsProcessos());
        this.dispararEmBackground('processos favoritos', () => ProcessoService.carregarProcessosFavoritos());
        this.dispararEmBackground('liberação de votos', () => ProcessoService.carregarLiberacaoVotos());
        this.dispararEmBackground('movimentos de minutas', () => MinutaService.carregarMovimentosDeMinutas());
        this.dispararEmBackground('situação de pauta', () => ProcessoService.carregarSituacaoDePautaTodosOsProcessosAsync());
        if (!ProcessoStore.carregouMovimentosPje && !ProcessoStore.carregandoMovimentosPje) {
            this.dispararEmBackground('movimentos PJe', () => ProcessoService.carregarMovimentosProcessosPjeAsync());
        }
        this.dispararEmBackground('última inclusão em pauta', () => ProcessoService.carregarUltimaInclusaoEmPautaTodosOsProcessosAsync());
        this.dispararEmBackground('resultado de julgamento', () => ProcessoService.carregarResultadoJulgamentoDeTodosOsProcessosAsync());
        this.dispararEmBackground('retorno de processos', () => ProcessoService.carregarRetornoProcessosTodosAsync());
        this.dispararEmBackground('petições pendentes', () => EscaninhoPeticaoService.carregarPeticoesPendentes(UsuarioStore.gabinete, new AbortController()));
        this.dispararEmBackground('tipo de julgamento', () => JulgamentoService.carregarTipoJulgamento());
        this.dispararEmBackground('impedimentos', () => ImpedimentoService.buscarImpedimentosDeTodosOsProcessos());
        this.dispararEmBackground('relacionamentos', () => ProcessoService.carregarRelacionamentosDeTodosOsProcessosAsync());
    }

    async funcoesDeCarregamentoDeProcesso(processo: Processo): Promise<void> {
        await Promise.all([
            ProcessoService.carregarAssuntosProcesso(processo),
            ProcessoService.carregarPartesProcesso(processo),
            ProcessoService.carregarEtiquetasProcesso(processo),
            ProcessoService.carregarTriagemLivreProcesso(processo),
            ProcessoService.carregarComentariosProcesso(processo),
            ProcessoService.carregarProcessosFavoritos(),
            ProcessoService.carregarRetornoProcesso(processo),
            MinutaService.carregarCategoriasDeMinutasDoProcesso(processo),
            MinutaService.carregarMovimentosDeMinuta(processo),
            ProcessoService.carregarMovimentosProcessosPjePorIdsProcessos([processo.identificadorProcesso]),
            JulgamentoService.carregarTipoJulgamento([processo.identificadorProcesso]),
            ProcessoService.carregarIndicadoresDeTodosOsProcessosAsyncPorIdsProcessos([processo.identificadorProcesso]),
            EscaninhoPeticaoService.carregarPeticoesPendentesPorProcesso(processo.identificadorProcesso)
        ]);

        MinutaService.consultaLiberacaoVoto(processo.minutas.find(minuta => minuta.isTipo(TipoMinuta.VOTO)));
    }

    abrirNoEscaninho = (
        idProcesso: number,
        controle: string,
        responsavel: string,
        tipoSituacaoMinuta: string,
        foraDoFluxo: string,
        controleNumero?: string
    ) => {
        if (idProcesso || controle || controleNumero || responsavel || tipoSituacaoMinuta) {
            FiltroStore.limparFiltros();
            if (idProcesso) {

                const proc = ProcessoStore._processos.find(processo => processo.identificadorProcesso === idProcesso);
                if (!proc) {
                    return;
                }

                // FiltroStore.listaDeNumeroProcessoFilter = proc.numeroProcesso;
                const numeroProcesso = UtilNumeroProcesso.matchObjetoNumeroProcesso(proc.numeroProcesso);
                FiltroStore.numeroProcessoFilter = numeroProcesso.numero;
                FiltroStore.digitoProcessoFilter = numeroProcesso.digito;
                FiltroStore.anoProcessoFilter = numeroProcesso.ano;
                FiltroStore.orgaoProcessoFilter = numeroProcesso.justica;
                FiltroStore.regionalProcessoFilter = numeroProcesso.tribunal;
                FiltroStore.varaProcessoFilter = numeroProcesso.vara;
                FiltroStore.foraDoGabineteFilter = foraDoFluxo === 'true';
            }
            if (controle) {
                FiltroStore.tarefaSelecionadaFilter.push(controle);
            }
            if (responsavel) {
                if (responsavel === 'null') {
                    FiltroStore.naoPossuiResponsavel = true;
                    FiltroStore.possuiResponsavel = false;
                } else {
                    FiltroStore.usuarioSelecionadoFilter.push(responsavel);
                }
            }
            if (tipoSituacaoMinuta) {
                if (tipoSituacaoMinuta == '70') {
                    FiltroStore.incluirForaDoGabineteFilter = true;
                }
                FiltroStore.situacaoMinutaFilter.push(SITUACOES_DE_MINUTA.find(sit => sit.codigo.toString() === tipoSituacaoMinuta).descricao);
            }

            GabineteStore.abrirEscaninho = true;
            EscaninhoStore.cardPrincipalExpanded = false;
            FiltroStore.procurar(false);
            if (controleNumero) {
                FiltroStore.filtroAplicado = true;
            }
        }
    };

    conferirQueryParams = () => {
        const filtrarPorId = RouterStore.getParam('idProcesso')[0];
        const foraDoFluxo = RouterStore.getParam('foraDoFluxo')[0];
        const filtrarPorControleTitulo = RouterStore.getParam('controleTitulo')[0];
        const filtrarPorControleNumero = RouterStore.getParam('controleNumero')[0];
        const filtrarPorResponsalvel = RouterStore.getParam('responsavel')[0];
        const filtrarPorSituacaoMinuta = RouterStore.getParam('situacaoMinuta')[0];

        const idProcesso = !filtrarPorId?.includes('Erro:') ? parseInt(filtrarPorId) : 0;
        const controle = !filtrarPorControleTitulo?.includes('Erro:')
            ? filtrarPorControleTitulo
            : '';
        const controleNumero = !filtrarPorControleNumero?.includes('Erro:')
            ? filtrarPorControleNumero
            : '';
        const responsavel = !filtrarPorResponsalvel?.includes('Erro:') ? filtrarPorResponsalvel : '';
        const tipoSituacaoMinuta = !filtrarPorSituacaoMinuta?.includes('Erro:') ? filtrarPorSituacaoMinuta : '';

        this.abrirNoEscaninho(
            idProcesso,
            controle,
            responsavel,
            tipoSituacaoMinuta,
            foraDoFluxo,
            controleNumero
        );
    }

    limpaSelecaoAgrupador = () => {
        EscaninhoStore.classeSelecionada = null;
        EscaninhoStore.faseSelecionada = null;
        EscaninhoStore.OJCSelecionado = null;
        EscaninhoStore.indicadorSelecionado = null;
    }

    filtrarEscaninhoPorAgrupadoresPadroes = (filtro: string, tipo: 'OJC' | 'indicador' | 'classe' | 'fase') => {
        FiltroStore.limparFiltros()
        if (tipo === 'classe') {
            FiltroStore.classeInicialProcessoFilter = []
            FiltroStore.classeInicialProcessoFilter.push(filtro);
            EscaninhoStore.expandedCardIndicadores = false;
            EscaninhoStore.expandedCardOJC = false;
            EscaninhoStore.expandedCardFase = false;
            this.limpaSelecaoAgrupador();
            EscaninhoStore.classeSelecionada = filtro;
        }
        if (tipo === 'indicador') {
            FiltroStore.indicadorSelecionadoFilter = []
            FiltroStore.indicadorSelecionadoFilter.push(filtro);
            EscaninhoStore.expandedCardClasse = false;
            EscaninhoStore.expandedCardOJC = false;
            EscaninhoStore.expandedCardFase = false;
            this.limpaSelecaoAgrupador();
            EscaninhoStore.indicadorSelecionado = filtro;
        }
        if (tipo === 'OJC') {
            FiltroStore.orgaoJudicanteSelecionadoFilter = []
            FiltroStore.orgaoJudicanteSelecionadoFilter.push(filtro);
            EscaninhoStore.expandedCardIndicadores = false;
            EscaninhoStore.expandedCardClasse = false;
            EscaninhoStore.expandedCardFase = false;
            this.limpaSelecaoAgrupador();
            EscaninhoStore.OJCSelecionado = filtro;
        }
        if (tipo === 'fase') {
            FiltroStore.classeProcessoFilter = []
            FiltroStore.classeProcessoFilter.push(filtro);
            EscaninhoStore.expandedCardIndicadores = false;
            EscaninhoStore.expandedCardClasse = false;
            EscaninhoStore.expandedCardOJC = false;
            this.limpaSelecaoAgrupador();
            EscaninhoStore.faseSelecionada = filtro;
        }
        FiltroStore.procurar(true);
        GabineteStore.abrirEscaninho = true;
    };
}

export default new EscaninhoService();
